/**
 * V2 Account Token API Service
 * V2에서는 토큰을 별도 API 없이 Account로 관리
 * - parentAccountId를 사용하여 토큰 계정 생성
 * - accountClassName = "TokenAccount"
 */

import {
  accountAPIServiceV2,
  type ICreateAccountParams,
} from './account-api.service'
import type { IAccountV2 } from '@/types/currency'

/**
 * 토큰 추가 파라미터
 */
export interface IAddTokenParams {
  walletId: string
  parentAccountId: string // 부모 계정 ID (예: Ethereum 계정)
  currencyId: string // 토큰 Currency ID (예: "ERC20/0x...")
  label?: string // 토큰 라벨 (선택)
  UVD?: {
    // Optional: 백엔드 DTO 검증을 위해 필요하지만 실제로는 무시됨
    b64SKey: string
    b64Data: string
  }
}

/**
 * 토큰 목록 조회 파라미터
 */
export interface IGetTokenListParams {
  parentAccountId: string // 부모 계정 ID
  walletId?: string // Wallet ID (선택)
}

export interface IAccountTokenAPIServiceV2 {
  addToken(params: IAddTokenParams): Promise<IAccountV2>
  getTokenList(params: IGetTokenListParams): Promise<IAccountV2[]>
  deleteToken(accountId: string): Promise<void>
  updateToken(accountId: string, label: string): Promise<IAccountV2>
}

export class AccountTokenAPIServiceV2 implements IAccountTokenAPIServiceV2 {
  /**
   * 토큰 추가 (V2)
   * V1: POST /account-token
   * V2: POST /wallet/:walletId/account (parentAccountId 포함)
   *
   * V2에서는 토큰도 일반 Account로 생성
   * - parentAccountId: 부모 계정 ID 지정
   * - accountClassName: "TokenAccount"로 자동 설정됨
   */
  async addToken(params: IAddTokenParams): Promise<IAccountV2> {
    const createParams: ICreateAccountParams = {
      accounts: [
        {
          currencyId: params.currencyId,
          accountIndex: 0, // 토큰은 accountIndex 0 사용
          label: params.label || 'Token Account',
          parentAccountId: params.parentAccountId,
        },
      ],
      UVD: params.UVD,
    }

    const accounts = await accountAPIServiceV2.createAccount(
      params.walletId,
      createParams,
    )

    // 첫 번째 계정 반환 (단일 토큰 생성)
    if (!accounts || accounts.length === 0) {
      throw new Error('Failed to create token account')
    }

    // IAccountInfo를 IAccountV2로 변환
    const accountInfo = accounts[0]
    return {
      id: accountInfo.id,
      walletId: accountInfo.walletId,
      currencyId: accountInfo.currencyId,
      addresses: accountInfo.addresses,
      balance: accountInfo.balance || '0',
      label: accountInfo.label,
      accountIndex: accountInfo.accountIndex,
      parentAccountId: params.parentAccountId,
      accountClassName: 'TokenAccount',
      used: false,
      lastSyncedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as IAccountV2
  }

  /**
   * 토큰 목록 조회 (V2)
   * V1: GET /account-token?accountId=xxx
   * V2: GET /account?walletId=xxx 후 parentAccountId로 필터링
   *
   * parentAccountId로 필터링하여 토큰 계정만 반환
   */
  async getTokenList(params: IGetTokenListParams): Promise<IAccountV2[]> {
    // walletId가 제공되지 않은 경우 에러
    if (!params.walletId) {
      throw new Error('walletId is required for getTokenList')
    }

    // 해당 wallet의 모든 계정 조회
    const allAccounts = await accountAPIServiceV2.getAllAccounts({
      walletId: params.walletId,
    })

    // parentAccountId가 일치하는 토큰 계정만 필터링
    const tokenAccounts = allAccounts.filter(
      (account) => account.parentAccountId === params.parentAccountId,
    )

    return tokenAccounts
  }

  /**
   * 토큰 삭제 (V2)
   * V1: DELETE /account-token/:id
   * V2: DELETE /wallet/{walletId}/account/{accountId}
   *
   * 일반 계정 삭제와 동일
   */
  async deleteToken(accountId: string, walletId?: string): Promise<void> {
    // walletId가 제공되지 않으면 localStorage에서 가져옴
    const targetWalletId = walletId || localStorage.getItem('defaultWalletId')
    if (!targetWalletId) {
      throw new Error('walletId is required for deleteToken')
    }
    await accountAPIServiceV2.deleteAccount(targetWalletId, accountId)
  }

  /**
   * 토큰 정보 업데이트 (V2)
   * V1: PATCH /account-token/:id/modify
   * V2: PUT /wallet/{walletId}/account/{accountId}
   *
   * 라벨 수정 등
   */
  async updateToken(accountId: string, label: string): Promise<IAccountV2> {
    const walletId = localStorage.getItem('defaultWalletId')
    if (!walletId) {
      throw new Error('walletId is required for updateToken')
    }
    const updated = await accountAPIServiceV2.updateAccount(
      walletId,
      accountId,
      {
        label,
      },
    )

    return updated
  }
}

// 싱글톤 인스턴스
export const accountTokenAPIServiceV2 = new AccountTokenAPIServiceV2()
